import { Mui, MuiIcons, DateFns } from "@osu/react-ui";
import { useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import { TOOLTIP_STATUS, TOOLTIPS, STUDENT_REQUEST_ACTIONS } from '../../util/constants';
import { addSpacesToCamelCase } from "../../util/functions";
import EasyCopy from "../../Common/EasyCopy";
import { ToolbarInternalActionsWithReset } from "../../Common/ToolbarInternalActionsWithReset";

export default function ClientSideGrid({
  rows,
  resultData,
  isFetching,
  selectedRows,
  onRowSelectionChange,
  columnVisibility,
  setColumnVisibility,
  columnOrder,
  setColumnOrder,
  sharedTableStyles,
  theme,
  renderTopToolbarContent,
  nestedFieldColumns,
  resetColumns,
  pageSizeInput,
  setPageSizeInput,
  paginationModel,
  setPaginationModel,
}) {
  function PendingCell({ row }) {
    const value = row?.original?.isPendingProcessing;
    if (value === true || value === 'Yes') {
      return <MuiIcons.HourglassEmpty fontSize="small" titleAccess="Pending Processing" sx={{ color: 'blue' }} />;
    }
    return <></>;
  }

  function renderNameLink({ row }) {
    const { applicantEmployeeId, programId, term } = row.original || {};
    if (!applicantEmployeeId) return null;
    const studentRequestIsActive = row.original.studentRequestIsActive === "Yes";
    const studentRequestType = row.original.studentRequestType;
    
    let tooltip = null;
    if (studentRequestIsActive) {
      if (studentRequestType === STUDENT_REQUEST_ACTIONS.APPEAL.label) {
        tooltip = TOOLTIPS(TOOLTIP_STATUS.APPEAL_REQUESTED);
      } else if (studentRequestType === STUDENT_REQUEST_ACTIONS.PROGRAM_CHANGE.label) {
        tooltip = TOOLTIPS(TOOLTIP_STATUS.PROGRAM_CHANGE_REQUESTED);
      } else if (studentRequestType === STUDENT_REQUEST_ACTIONS.WITHDRAW.label) {
        tooltip = TOOLTIPS(TOOLTIP_STATUS.WITHDRAW_REQUESTED);
      }
    }
    return (
      <>
      <EasyCopy contents={applicantEmployeeId} />
      <Mui.Link
        component={Link}
        to={`/applicant/${applicantEmployeeId}/${programId}/${term}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'underline' }}
      >
        {applicantEmployeeId}{tooltip}
      </Mui.Link>
      </>
    );
  }

  const builtColumns = useMemo(() => {
    if (!resultData?.columns || resultData.columns.length === 0) {
      return [];
    }

      return resultData?.columns
        .filter((column) => !column.field.includes('.'))
        .map((column) => {
          const isApplicantId = column.field === 'applicantEmployeeId';

          const base = {
          id: isApplicantId ? "id" : column.field,
          header: column.headerName,
          accessorKey: column.field,
          minSize: 80,
          size: column.width || undefined,
          enableResizing: true,
          Cell: column.field === 'isPendingProcessing' ? PendingCell : (column.renderCell === "renderNameLink" ? renderNameLink : undefined),
          default: column.default,
          detailHeaderName: column.detailHeaderName,
        };
        return base;
      });
   
  }, [resultData]);

  const renderDetailPanel = useCallback(({ row }) => {
    const parentFields = Object.keys(nestedFieldColumns);
    if (parentFields.length === 0) return null;
    
    const hasNestedData = parentFields.some(parentField => {
      const data = row.original[parentField];
      return Array.isArray(data) && data.length > 0;
    });
    
    if (!hasNestedData) return null;
    
    return (
      <Mui.Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 3,
          padding: 2,
          backgroundColor: theme.palette.mode === 'dark' ? '#444' : '#f5f5f5',
          flexWrap: 'wrap',
          width: '100%',
        }}
      >
        {parentFields.map(parentField => {
          const data = row.original[parentField];
          if (!Array.isArray(data) || data.length === 0) return null;
          
          const cols = nestedFieldColumns[parentField];
          
          return (
            <Mui.Box key={parentField} sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Mui.Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                {addSpacesToCamelCase(parentField)}:
              </Mui.Typography>
              <Mui.Box sx={{ pl: 2 }}>
                {data.map((item, idx) => (
                  <Mui.Box
                    key={idx}
                    sx={{
                      mb: idx < data.length - 1 ? 1.5 : 0,
                      pb: idx < data.length - 1 ? 1.5 : 0,
                      borderBottom: idx < data.length - 1 ? '1px solid' : 'none',
                      borderColor: theme.palette.mode === 'dark' ? '#555' : '#ddd',
                    }}
                  >
                    {cols.map(col => {
                      const fieldName = col.field.split('.')[1];
                      const value = item[fieldName];
                      const displayName = col.detailHeaderName || addSpacesToCamelCase(fieldName);
                      
                      if (value === undefined || value === null || value === '') return null;
                      
                      return (
                        <Mui.Typography key={col.field} variant="body2" sx={{ mb: 0.5 }}>
                          <strong>{displayName}:</strong> {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </Mui.Typography>
                      );
                    })}
                  </Mui.Box>
                ))}
              </Mui.Box>
            </Mui.Box>
          );
        })}
      </Mui.Box>
    );
  }, [nestedFieldColumns, theme]);

  return (
    <DateFns.LocalizationProvider>
      <MaterialReactTable
        enableStickyHeader={true}
        data={rows}
        columns={builtColumns}
        getRowId={(row) => row.applicantEmployeeId}
        enableColumnResizing={true}
        enableExpanding={Object.keys(nestedFieldColumns).length > 0}
        renderDetailPanel={Object.keys(nestedFieldColumns).length > 0 ? renderDetailPanel : undefined}
        enableRowSelection={(row) => {
          const raw = row?.original?.isPendingProcessing;
          return !(raw === true || raw === 'Yes');
        }}
        muiSelectCheckboxProps={(row) => {
          const raw = row?.row?.original?.isPendingProcessing;
          const disabled = raw === true || raw === 'Yes';
          return disabled ? { disabled: true, sx: { opacity: 0.3 } } : {};
        }}
        muiTableBodyRowProps={({ row }) => {
          const raw = row?.original?.isPendingProcessing;
          if (raw === true || raw === 'Yes') {
            return { sx: { opacity: 0.5 } };
          }
          return {};
        }}
        enableColumnPinning
        columnResizeMode="onChange"
        enableColumnOrdering
        enableColumnDragging
        enableGlobalFilter={false}
        positionToolbarAlertBanner="bottom"
        onRowSelectionChange={onRowSelectionChange}
        renderTopToolbarCustomActions={renderTopToolbarContent}
        enableRowVirtualization
        muiTableContainerProps={{ sx: { maxHeight: '600px' } }}
        muiTableBodyCellProps={{ sx: sharedTableStyles }}
        muiTableHeadCellProps={{
          sx: sharedTableStyles,
          onKeyDown: (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
            }
          }
        }}
        muiTopToolbarProps={{ sx: sharedTableStyles }}
        muiBottomToolbarProps={{ sx: sharedTableStyles }}
        state={{
          isLoading: isFetching,
          rowSelection: selectedRows,
          columnVisibility,
          columnOrder,
          pagination: paginationModel
        }}
        onColumnVisibilityChange={setColumnVisibility}
        onColumnOrderChange={setColumnOrder}
        renderToolbarInternalActions={({ table }) => (
          <ToolbarInternalActionsWithReset table={table} onResetColumns={resetColumns} />
        )}
        manualPagination
        onPaginationChange={setPaginationModel}
        rowCount={resultData?.totalCount ?? rows.length}
        renderBottomToolbarCustomActions={() => (
          <Mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Mui.Typography variant="body2">Page Size:</Mui.Typography>
            <Mui.TextField
              type="number"
              size="small"
              value={pageSizeInput}
              onChange={(e) => setPageSizeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const value = parseInt(e.target.value, 10);
                  if (value > 0) {
                    setPaginationModel({ ...paginationModel, pageSize: value, pageIndex: 0 });
                  }
                }
              }}
              onBlur={(e) => {
                const value = parseInt(e.target.value, 10);
                if (value > 0) {
                  setPaginationModel({ ...paginationModel, pageSize: value, pageIndex: 0 });
                }
              }}
              inputProps={{ min: 1, max: 1000 }}
              sx={{ width: 100 }}
            />
          </Mui.Box>
        )}
      />
    </DateFns.LocalizationProvider>
  );
}
